import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert, Box, Button, Container, Stack, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';

import { toPromoBrand } from '~/consts/promoBrand';

import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { useListVoucherAutoAssignmentRules } from '~/hooks/apis/promo/useListVoucherAutoAssignmentRules';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import { decodePathParam } from '~/utils/pathParams';

import VoucherAutoAssignRuleGroupRuleAccordion from './VoucherAutoAssignRuleGroupRuleAccordion';

export default function VoucherAutoAssignRuleGroupPage() {
  const { push } = useAppNavigation();
  const params = useParams<{ assignmentGroup?: string }>();
  const { tenant } = useCurrentTenant();
  const { promoMeta } = usePromoMeta();

  const assignmentGroup = decodePathParam(params.assignmentGroup);
  const brand = toPromoBrand(tenant.brand);

  const listQuery = useMemo(
    () => ({
      assignment_group: assignmentGroup,
      ...(brand ? { brand } : {}),
      page: 0,
      limit: 100,
    }),
    [assignmentGroup, brand],
  );

  const { data, isLoading, isError, error } = useListVoucherAutoAssignmentRules({
    query: listQuery,
    enabled: Boolean(assignmentGroup),
  });

  const rules = useMemo(
    () =>
      [...(data?.result.rules ?? [])].sort((a, b) => {
        const applyOrderA = a.applyOrder ?? Number.NEGATIVE_INFINITY;
        const applyOrderB = b.applyOrder ?? Number.NEGATIVE_INFINITY;
        if (applyOrderA !== applyOrderB) {
          return applyOrderB - applyOrderA;
        }
        return a.voucherAutoAssignmentRuleId.localeCompare(b.voucherAutoAssignmentRuleId);
      }),
    [data?.result.rules],
  );
  const mostRecentlyCreatedRule = useMemo(
    () =>
      [...rules].sort((a, b) => {
        const createdAtA = Date.parse(a.createdAt);
        const createdAtB = Date.parse(b.createdAt);
        return createdAtB - createdAtA;
      })[0],
    [rules],
  );
  const voucherIds = useMemo(() => Array.from(new Set(rules.map((rule) => rule.voucherId))), [rules]);
  const createRulePath = useMemo(() => {
    const params = new URLSearchParams({ assignment_group: assignmentGroup });
    if (mostRecentlyCreatedRule) {
      params.set('source_rule_id', mostRecentlyCreatedRule.voucherAutoAssignmentRuleId);
    }
    return `/marketing/voucher-auto-assign-rules/create?${params.toString()}`;
  }, [assignmentGroup, mostRecentlyCreatedRule]);
  const {
    data: vouchersMap,
    isLoading: isVouchersLoading,
    isError: isVouchersError,
  } = useGetVouchersByIds({
    voucherIds,
    brand: tenant.brand,
    enabled: voucherIds.length > 0,
  });

  return (
    <>
      <Helmet>
        <title>Marketing / Voucher auto-assign group / {assignmentGroup}</title>
      </Helmet>
      <Container sx={{ mt: 2, mb: 4 }}>
        <PageHeader title="Voucher auto-assign group">
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="text" color="primary" onClick={() => push('/marketing/voucher-auto-assign-rules')}>
              Back to all rules
            </Button>
            <Button variant="contained" color="primary" onClick={() => push(createRulePath)} disabled={isLoading}>
              Create rule
            </Button>
          </Stack>
        </PageHeader>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing rules with assignment group{' '}
            <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
              {assignmentGroup}
            </Typography>
            . Rules are ordered by highest apply order first.
          </Typography>
        </Box>

        {(isLoading || isVouchersLoading) && <Spinner />}
        {isError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error instanceof Error ? error.message : 'Failed to load voucher auto-assign rules'}
          </Alert>
        ) : null}
        {isVouchersError ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Some linked voucher details could not be loaded.
          </Alert>
        ) : null}

        {!isLoading && !isError ? (
          <Stack spacing={1.5} sx={{ mt: 3 }}>
            {rules.map((rule) => (
              <VoucherAutoAssignRuleGroupRuleAccordion
                key={rule.voucherAutoAssignmentRuleId}
                rule={rule}
                voucher={vouchersMap?.get(rule.voucherId)}
                brand={tenant.brand}
                promoMeta={promoMeta}
              />
            ))}
            {rules.length === 0 ? <Alert severity="info">No rules found for this assignment group.</Alert> : null}
          </Stack>
        ) : null}
      </Container>
    </>
  );
}
